import React, { useEffect, useRef, useState } from 'react';

import {
    formatMapEmbedUrl,
    formatMapUrl
} from '$utilities/format.js';

import { IconArrowOutwards } from '$partials/icon/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/ui.d.ts').Map}
 */
const Map = ({
    label,
    latitude,
    longitude,
    zoom
}) => {
    const frameRef = useRef(null);

    const [box, setBox] = useState(null);

    // An embed is given the ground it covers rather than a centre and a zoom,
    // so the box it is drawn in is measured and the bounding box worked out
    // against that. Until it is - on the server, and for the first render - the
    // design's desktop box stands in, which the frame is lazy about loading.
    useEffect(() => {
        const frame = frameRef.current;

        if (!frame || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const observer = new ResizeObserver(([entry]) => {
            setBox({
                height: entry.contentRect.height,
                width: entry.contentRect.width
            });
        });

        observer.observe(frame);

        return () => observer.disconnect();
    }, []);

    const position = { latitude, longitude, zoom };
    const embedUrl = formatMapEmbedUrl(position, box);

    if (!embedUrl) {
        return null;
    }

    return (
        <div
            className='relative aspect-[5/4] sm:aspect-[3/2] bg-neutral-50'
            ref={frameRef}>
            {/* Greyscale until the designer settles on a style of their own */}
            <iframe
                className='absolute inset-0 h-full w-full border-0 grayscale'
                loading='lazy'
                src={embedUrl}
                title={label} />

            <a
                className='button-round absolute bottom-10 right-16'
                href={formatMapUrl(position)}
                rel='noopener noreferrer'
                target='_blank'>
                <span className='sr-only'>{label} on a larger map</span>

                <span aria-hidden={true}>
                    <IconArrowOutwards
                        height={20}
                        width={20} />
                </span>
            </a>
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default Map;
